import { setSimulateUserShortcutModal } from '@/slices/modals';
import { useDispatch, useSelector } from '@/store';
import { useLazyQuery, useMutation } from '@apollo/client';
import ModalActions from '@components/modal/ModalActions';
import ModalTitle from '@components/modal/ModalTitle';
import { Access, AuthorizationWrapper } from '@features/authorization';
import { msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import {
  Autocomplete,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  Skeleton,
  TextField,
} from '@mui/material';
import { FormEventHandler, Suspense, useEffect, useState, type FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useModalStatusMessage } from 'src/hooks';
import type { User } from 'src/types/user';
import SIMULATE_USER from '../../graphql/mutations/simulateUser';
import GET_USER_LIST, { type UserList_User } from '../../graphql/queries/getUserList';

/** Cantidad de opciones que trae el autocomplete por búsqueda. */
const SEARCH_LIMIT = 10;
/** Espera antes de disparar la búsqueda mientras se escribe. */
const SEARCH_DEBOUNCE_MS = 300;
/** Largo mínimo del término antes de consultar al backend. */
const MIN_SEARCH_LENGTH = 2;

interface Props {
  onClose: () => void;
}

const Component: FC<Props> = ({ onClose }) => {
  const navigate = useNavigate();
  const { _ } = useLingui();
  const { openModalErrorMessage } = useModalStatusMessage();
  const { simulate, isSimulating, stopUserSimulation } = useAuth();
  const [inputValue, setInputValue] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserList_User | null>(null);

  const [searchUsers, { data, loading: searching }] = useLazyQuery(GET_USER_LIST, {
    onError: (error) => {
      openModalErrorMessage(_(msg`No se pudo buscar usuarios`) + ': ' + error.message);
    },
  });

  useEffect(() => {
    const search = inputValue.trim();
    if (search.length < MIN_SEARCH_LENGTH) return;

    const timeout = setTimeout(() => {
      searchUsers({
        variables: {
          input: { search },
          pagination: { limit: SEARCH_LIMIT },
        },
      });
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeout);
  }, [inputValue, searchUsers]);

  const [simulateMutation, { loading }] = useMutation(SIMULATE_USER, {
    onCompleted: async (data) => {
      const { impersonateUser: userToImpersonate } = data;
      if (!userToImpersonate) {
        openModalErrorMessage(_(msg`No se pudo simular al usuario`));
        return;
      }
      if (isSimulating) {
        stopUserSimulation();
      }
      // impersonateUser (tipo GQL legacy User) no expone twoFactorAuth/createdAt del dominio User; cast localizado sin cambio de runtime
      simulate(userToImpersonate as unknown as User);
      navigate({
        search: '',
      });
      navigate(0);
    },
    onError(error) {
      openModalErrorMessage(_(msg`No se pudo simular al usuario`) + ': ' + error.message);
    },
  });

  const options = data?.usersPagination.items ?? [];

  const onSubmit: FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault();
    if (!selectedUser) return;
    await simulateMutation({
      variables: {
        userId: selectedUser.id,
      },
    });
  };

  return (
    <form onSubmit={onSubmit}>
      <DialogContent dividers>
        <Autocomplete
          options={options}
          value={selectedUser}
          inputValue={inputValue}
          loading={searching}
          onInputChange={(_event, newInputValue) => setInputValue(newInputValue)}
          onChange={(_event, newValue) => setSelectedUser(newValue)}
          isOptionEqualToValue={(option, selectedOption) => option.id === selectedOption.id}
          getOptionLabel={(option) => `${option.fullName} (${option.email})`}
          filterOptions={(availableOptions) => availableOptions}
          noOptionsText={
            inputValue.trim().length < MIN_SEARCH_LENGTH
              ? _(msg`Escribe al menos 2 caracteres`)
              : _(msg`Sin resultados`)
          }
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {option.fullName} ({option.email})
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              autoFocus
              label={_(msg`Buscar usuario`)}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {searching ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </DialogContent>
      <ModalActions
        onClose={onClose}
        onSubmit="submit"
        submitLoading={loading}
        submitDisabled={!selectedUser}
        submitLabel={_(msg`Simular`)}
      />
    </form>
  );
};

/**
 * Simulación de usuario abierta por el shortcut global (`useGlobalShortcuts`).
 * A diferencia de `SimulateUserModal`, no recibe un `userId`: lo resuelve internamente
 * con un autocomplete cross-organización. Gateado por `Access.GlobalShortcuts` (solo AmaruGod).
 */
const SimulateUserShortcutModal: FC = () => {
  const dispatch = useDispatch();
  const { _ } = useLingui();
  const { simulateUserShortcutModal } = useSelector((state) => state.modals_store);

  const onClose = () => {
    dispatch(
      setSimulateUserShortcutModal({
        open: false,
      }),
    );
  };

  return (
    <Dialog open={simulateUserShortcutModal.open} onClose={onClose} fullWidth maxWidth="sm">
      <ModalTitle onClose={onClose} title={_(msg`Simular usuario`)} />
      <Suspense
        fallback={
          <Box>
            <Skeleton variant="rounded" height={100} />
            <ModalActions onClose={onClose} />
          </Box>
        }
      >
        <AuthorizationWrapper onClose={onClose} access={Access.GlobalShortcuts}>
          {simulateUserShortcutModal.open && <Component onClose={onClose} />}
        </AuthorizationWrapper>
      </Suspense>
    </Dialog>
  );
};

export default SimulateUserShortcutModal;
